import { Icon } from "@iconify/react";
import Classnames from "classnames";
import DeepEqual from "deep-equal";
import React, { useEffect, useState, useRef } from "react";

import i18n from "../utils/i18n";
import Button from "./button";
import Checkbox from "./checkbox";
import Chip from "./chip";
import Portal from "./portal";
import style from "./select.styl";
import Textbox from "./textbox";
import Tooltip from "./tooltip";

type OptionElement = React.ReactElement<{
  value: string;
  children: string;
}>;

type SelectProps = {
  children?: OptionElement | OptionElement[];
  className?: string;
  disabled?: boolean;
  /**
   * Fill parent width
   */
  fill?: boolean;
  /**
   * Optional. Triggers scrollbar if exceeded
   */
  height?: string;
  loading?: boolean;
  onChange?: (value: string | string[]) => void;
  /**
   * Can select multiple options
   */
  multi?: boolean;
  /**
   * @default 'large'
   */
  size?: "small" | "medium" | "large";
  value?: string | string[];
  error?: React.ReactNode;
  hint?: string;
  label?: string;
  placeholder?: string;
  noType?: boolean;
  unBlur?: boolean;
  autoBlur?: boolean;
  portal?: boolean;
  portalClassName?: string;
  /**
   * Forwarded to the inner 'input', so it gets `data-testid="input-form-${name}"`
   */
  name?: string;
};

const Select = ({
  children,
  className,
  disabled,
  fill,
  height: heightProp,
  loading,
  onChange: onChangeProp,
  multi,
  size = "large",
  value: valueProp,
  error,
  hint,
  label,
  placeholder,
  noType,
  unBlur,
  autoBlur,
  portal,
  portalClassName,
  name,
}: SelectProps) => {
  const [isOpen, setOpen] = useState<boolean>();
  const [options, setOptions] = useState<OptionElement[]>(
    React.Children.toArray(children).filter(
      (child): child is OptionElement =>
        React.isValidElement(child) && child.type === "option",
    ),
  );
  const [searchOptions, setSearchOptions] = useState(options);
  const [selected, setSelected] = useState<OptionElement | OptionElement[]>(
    multi
      ? valueProp
        ? options.filter((option) =>
            Array.isArray(valueProp)
              ? valueProp.includes(option.props.value)
              : false,
          )
        : []
      : options.find((option) => option.props.value === valueProp),
  );
  const [searchValue, setSearchValue] = useState<string>();
  const [keyboardSelect, setKeyboardSelect] = useState(
    multi ? -2 : options.findIndex((option) => option === selected),
  );
  const [hiddenCount, setHiddenCount] = useState(0);
  const [hiddenOptions, setHiddenOptions] = useState<string[]>([]);
  const [visibleOptionsWidth, setVisibleOptionsWidth] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleResize = () => {
    setHiddenCount(0);
    if (selectRef.current) {
      const optionSpans = Array.from(selectRef.current.querySelectorAll("div"));
      const spanWidths = optionSpans.map(
        (option) => option.getBoundingClientRect().width,
      );
      const totalWidth = selectRef.current.getBoundingClientRect().width - 72;

      let width = 0;
      let visible = 0;

      for (let i = 0; i < spanWidths.length; i++) {
        width += spanWidths[i] + 4;

        if (width > totalWidth) {
          break;
        }
        visible++;
      }

      setHiddenCount(spanWidths.length - visible);
      setHiddenOptions(
        optionSpans
          .slice(-(spanWidths.length - visible))
          .map((span) => span.innerText),
      );
      setVisibleOptionsWidth(
        visible < spanWidths.length ? width + 20 - spanWidths[visible] : 0,
      );
    }
  };

  useEffect(() => {
    const opts = React.Children.toArray(children).filter(
      (child): child is OptionElement =>
        React.isValidElement(child) && child.type === "option",
    );

    if (DeepEqual(opts, options)) return;

    setOptions(opts);
    setSearchOptions(
      searchValue
        ? opts.filter((option) =>
            option.props.children
              .toLowerCase()
              .includes(searchValue.toLowerCase()),
          )
        : opts,
    );
  }, [children]);

  useEffect(() => {
    if (multi) {
      const newSelected = valueProp
        ? options.filter((option) =>
            Array.isArray(valueProp)
              ? valueProp.includes(option.props.value)
              : false,
          )
        : [];
      // Only update if the option elements changed (e.g., label changed)
      const currentSelected = selected as OptionElement[];
      const hasChanged =
        newSelected.length !== currentSelected?.length ||
        newSelected.some(
          (opt, i) =>
            opt.props.value !== currentSelected[i]?.props.value ||
            opt.props.children !== currentSelected[i]?.props.children,
        );
      if (hasChanged) {
        setSelected(newSelected);
      }
    } else {
      const newSelected = options.find(
        (option) => option.props.value === valueProp,
      );
      const currentSelected = selected as OptionElement | undefined;
      // Only update if the label changed
      if (
        newSelected?.props.children !== currentSelected?.props.children ||
        newSelected?.props.value !== currentSelected?.props.value
      ) {
        setSelected(newSelected);
      }
    }
  }, [options]);

  useEffect(() => {
    if (!isOpen && multi) {
      setKeyboardSelect(-2);
      setTimeout(handleResize, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    handleResize();
    setTimeout(() => handleResize(), 100);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (multi) {
      const newSelected = valueProp
        ? searchOptions.filter((option) =>
            Array.isArray(valueProp)
              ? valueProp.includes(option.props.value)
              : false,
          )
        : [];

      if (!DeepEqual(newSelected, selected)) {
        setSelected(newSelected);
      }
    } else {
      const selectedOption = selected as OptionElement | undefined;
      if (!selectedOption || valueProp !== selectedOption.props.value) {
        selectOption(
          searchOptions.find((option) => option.props.value === valueProp),
        );
      }
      if (typeof valueProp === "undefined") {
        setSelected(undefined);
      }
    }
  }, [valueProp]);

  useEffect(() => {
    if (multi) {
      // do I need to do something here? Maybe not
    } else {
      if (keyboardSelect > -1 && keyboardSelect < searchOptions.length) {
        selectOption(searchOptions[keyboardSelect]);
        if (scrollRef.current) {
          scrollRef.current.scrollTop = keyboardSelect * 35 + 14;
        }
      } else {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      }
    }
  }, [keyboardSelect]);

  useEffect(() => {
    if (searchValue) {
      const index = options.findIndex((option) =>
        option.props.children
          .toLowerCase()
          .startsWith(searchValue.toLowerCase()),
      );

      if (!multi) {
        const opts = options.filter((option) =>
          option.props.children
            .toLowerCase()
            .includes(searchValue.toLowerCase()),
        );
        setSearchOptions(opts);
      } else {
        if (index > -1) {
          setKeyboardSelect(index);
        }
      }
    } else {
      const selectedOption = selected as OptionElement | undefined;
      if (selectedOption) {
        setKeyboardSelect(
          options.findIndex(
            (option) =>
              option.props.value ===
              (selectedOption.props && selectedOption.props.value),
          ),
        );
      } else {
        setKeyboardSelect(-1);
      }
      setSearchOptions(options);
    }
  }, [searchValue]);

  useEffect(() => {
    if (selected) {
      if (typeof onChangeProp === "function") {
        onChangeProp(
          multi
            ? (selected as OptionElement[]).map((option) => option.props.value)
            : (selected as OptionElement).props.value,
        );
      }
      handleResize();
      setTimeout(() => handleResize(), 100);
    }
  }, [selected]);

  const selectOption = (option: OptionElement | undefined) => {
    if (typeof option === "undefined") return;

    if (multi) {
      const selectedArray = selected as OptionElement[];
      if (hasOption(selectedArray, option)) {
        setSelected((selected) =>
          (selected as OptionElement[]).filter(
            (o) => o.props.value !== option.props.value,
          ),
        );
      } else {
        setSelected((selected) => [...(selected as OptionElement[]), option]);
      }
    } else {
      setSelected(option);
      setKeyboardSelect(searchOptions.indexOf(option));
    }
  };

  // options might be different entities after a render so check values
  const hasOption = (
    optionList: OptionElement[] | undefined,
    option: OptionElement,
  ) =>
    optionList &&
    option &&
    optionList.some((o) => o && o.props.value === option.props.value);

  const classNameMerged = Classnames(
    style.select,
    {
      [style.fill]: fill,
    },
    className,
  );

  const position = () => {
    const textarea = textareaRef.current;
    const dropdown = dropdownRef.current;

    if (textarea && dropdown) {
      const textareaBounds = textarea.getBoundingClientRect();

      let x = 0;
      let y = textareaBounds.height;

      if (portal) {
        x = textareaBounds.left + window.document.documentElement.scrollLeft;
        y = textareaBounds.bottom + window.document.documentElement.scrollTop;
      }

      dropdown.style.display = isOpen ? "block" : "none";
      const pastBottom =
        window.document.documentElement.offsetHeight -
        14 -
        (textareaBounds.bottom + dropdown.getBoundingClientRect().height);

      if (pastBottom < 0) {
        y += pastBottom;
      }

      return {
        transform: `translate(${x}px, ${y}px)`,
        minWidth: `${textareaBounds.width}px`,
      };
    }

    return {};
  };

  const height = () =>
    typeof heightProp !== "undefined"
      ? { maxHeight: heightProp, overflow: "auto" as const, padding: "1rem" }
      : {
          maxHeight: "calc(100vh - 3rem)",
          overflow: "auto" as const,
          padding: "1rem",
        };

  const renderDropdown = () => (
    <div
      ref={dropdownRef}
      className={Classnames(style.dropdown, { [style.hidden]: !isOpen })}
      style={position()}
    >
      <div ref={scrollRef} style={height()} className={style.dropdownInner}>
        <div>
          {multi && (
            <Button
              as="button"
              data-testid={name ? `option-form-${name}-all` : undefined}
              className={Classnames(style.option, {
                [style.multi]: multi,
                [style.keyboardSelect]: keyboardSelect === -1,
                [style.selected]:
                  selected &&
                  (selected as OptionElement[]).length === options.length,
              })}
              onMouseDown={(e) => {
                (e.target as HTMLElement).focus();
                e.preventDefault();
                if ((selected as OptionElement[]).length === options.length) {
                  setSelected([]);
                } else {
                  setSelected(options);
                }
              }}
            >
              <Checkbox
                tabIndex={-1}
                value={(selected as OptionElement[]).length === options.length}
                className={style.checkbox}
              >
                {i18n.t("common:all")}
              </Checkbox>
            </Button>
          )}
          {searchOptions.map((option) => {
            return (
              <Button
                as="button"
                key={option.props.value}
                data-testid={
                  name ? `option-form-${name}-${option.props.value}` : undefined
                }
                className={Classnames(style.option, {
                  [style.multi]: multi,
                  [style.keyboardSelect]:
                    option === searchOptions[keyboardSelect],
                  [style.selected]: multi
                    ? hasOption(selected as OptionElement[], option)
                    : option.props.value ===
                      ((selected as OptionElement)?.props?.value ?? undefined),
                })}
                onMouseDown={(e) => {
                  (e.target as HTMLElement).focus();
                  e.preventDefault();
                  selectOption(option);
                  setSearchValue(undefined);
                  if (!multi) setOpen(false);
                }}
              >
                {multi ? (
                  <Checkbox
                    tabIndex={-1}
                    className={style.checkbox}
                    value={hasOption(selected as OptionElement[], option)}
                  >
                    <Tooltip
                      type="custom"
                      contents={option.props.children}
                      position="in-place"
                      nofocus
                    >
                      {option.props.children}
                    </Tooltip>
                  </Checkbox>
                ) : (
                  <Tooltip
                    type="custom"
                    contents={option.props.children}
                    position="in-place"
                    nofocus
                  >
                    {option.props.children}
                  </Tooltip>
                )}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const selectedArray = selected as OptionElement[];
  const selectedSingle = selected as OptionElement | undefined;

  return (
    <div
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
          setSearchValue(undefined);
        }
      }}
      onMouseUp={() =>
        !disabled &&
        !isOpen &&
        (window.document.activeElement as HTMLElement)?.blur()
      }
      className={classNameMerged}
    >
      {!isOpen && multi && selectedArray && selectedArray.length > 0 && (
        <>
          <div ref={selectRef} className={style.selectedChips}>
            {selectedArray.map((option) => (
              <Chip key={option.props.value} bgColor="var(--neutral-200)">
                {option.props.children}
              </Chip>
            ))}
          </div>
          {hiddenCount > 0 && (
            <Tooltip
              position="top"
              className={style.tooltip}
              type="light"
              delay="0s"
              contents={hiddenOptions.join(", ")}
            >
              <Chip
                className={style.hiddenOptionsChip}
                borderColor="var(--primary-400)"
                bgColor="var(--primary-200)"
                textColor="var(--primary-600)"
                shape="circle"
                style={{
                  left: visibleOptionsWidth - 12 + "px",
                }}
              >
                {`+${hiddenCount}`}
              </Chip>
            </Tooltip>
          )}
        </>
      )}
      <Textbox
        fill
        name={name}
        autocomplete="nope"
        readOnly={noType}
        normalStyle
        size={size}
        ref={textareaRef}
        unBlur={unBlur}
        autoBlur={autoBlur}
        disabled={disabled || loading}
        error={error}
        hint={hint}
        label={label}
        placeholder={
          label
            ? undefined
            : multi
              ? selectedArray.length > 0
                ? ""
                : i18n.t("common:select")
              : typeof selectedSingle !== "undefined"
                ? selectedSingle.props.children
                : (placeholder ?? i18n.t("common:select"))
        }
        suffixIcon={
          loading ? (
            <div className="loadingSpinner" />
          ) : (
            <Icon
              icon="feather:chevron-down"
              className={Classnames(style.chevron, { [style.open]: isOpen })}
            />
          )
        }
        value={
          isOpen
            ? searchValue
            : multi
              ? ""
              : typeof selectedSingle !== "undefined"
                ? selectedSingle.props.children
                : ""
        }
        onChange={(value) =>
          setSearchValue(typeof value === "number" ? String(value) : value)
        }
        onBlur={() => setSearchValue(undefined)}
        onEnter={(e) => {
          setSearchValue(undefined);
          if (!multi) {
            setOpen((open) => !open);
            (e.target as HTMLInputElement).blur();
          }
        }}
        onKeyDown={(e) => {
          if (!isOpen) {
            setOpen(true);
          }
          switch (e.key) {
            case "Up":
            case "ArrowUp": {
              setKeyboardSelect((keyboardSelect) =>
                Math.max(multi ? -1 : 0, keyboardSelect - 1),
              );
              e.preventDefault();
              break;
            }
            case "Down":
            case "ArrowDown": {
              setKeyboardSelect((keyboardSelect) =>
                Math.min(searchOptions.length - 1, keyboardSelect + 1),
              );
              e.preventDefault();
              break;
            }
            case "Escape":
              (window.document.activeElement as HTMLElement)?.blur();
              break;
            case "Enter":
              if (multi && keyboardSelect === -1) {
                if (
                  (selected as OptionElement[]).length === searchOptions.length
                ) {
                  setSelected([]);
                } else {
                  setSelected(searchOptions);
                }
              } else {
                selectOption(searchOptions[keyboardSelect]);
              }
              break;
          }
        }}
      />
      {portal ? (
        <Portal className={portalClassName}>{renderDropdown()}</Portal>
      ) : (
        renderDropdown()
      )}
    </div>
  );
};

Select.displayName = "Select";

export default Select;
